import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Lang, translate } from '../i18n/strings';

export type ThemeChoice = 'system' | 'light' | 'dark';

interface Preferences {
  lang: Lang;
  setLang: (l: Lang) => void;
  themeChoice: ThemeChoice;
  setThemeChoice: (t: ThemeChoice) => void;
  /** What is actually shown */
  mode: 'light' | 'dark';
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LANG_KEY = 'medease.lang';
const THEME_KEY = 'medease.theme';

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
};

const Ctx = createContext<Preferences | null>(null);

/** Language and light/dark theme, per device. */
export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [lang, setLangState] = useState<Lang>(() => {
    const v = read(LANG_KEY);
    return v === 'hi' || v === 'mr' ? v : 'en';
  });
  const [themeChoice, setThemeState] = useState<ThemeChoice>(() => {
    const v = read(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    write(LANG_KEY, l);
  }, []);
  const setThemeChoice = useCallback((t: ThemeChoice) => {
    setThemeState(t);
    write(THEME_KEY, t);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en-IN' : lang;
  }, [lang]);

  const mode = themeChoice === 'system' ? (systemDark ? 'dark' : 'light') : themeChoice;
  const t = useCallback((key: string, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang]);
  const value = useMemo(() => ({ lang, setLang, themeChoice, setThemeChoice, mode, t }), [lang, setLang, themeChoice, setThemeChoice, mode, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const usePreferences = (): Preferences => {
  const c = useContext(Ctx);
  if (!c) {
    // Outside the provider (isolated tests): English, light
    return { lang: 'en', setLang: () => {}, themeChoice: 'light', setThemeChoice: () => {}, mode: 'light', t: (k, v) => translate('en', k, v) };
  }
  return c;
};

export const useT = () => usePreferences().t;
